import type { Color, FileUploadFormFieldValue, PhoneFormFieldValue } from "@tournated-com/ui";

import { Language } from "@/i18n/i18n.types";
import { BaseClub } from "@/types/club.types";
import { CountryCode, CurrencyCode, DateFormat, Id, TimeFormat } from "@/types/common.types";
import { Discipline } from "@/types/discipline.types";
import { Document, DocumentFolder } from "@/types/document.types";
import {
  Organization,
  OrganizationReference,
  OrganizationWithManagers,
} from "@/types/organization.types";
import { FeesSegment, FeeType, ServiceType, TournatedFeeVat } from "@/types/payment.types";
import { Sport, SportRef } from "@/types/sport.types";
import { TerminologySettings } from "@/types/terminology.types";

import { ThirdPartyLicense, ThirdPartyLicenseReference } from "./user.types";

export type PlatformId = string;

export const PlatformStatusValues = ["active", "inactive"] as const;
export type PlatformStatus = (typeof PlatformStatusValues)[number];

export const PlatformTypeValues = [
  "governingBody",
  "marketplace",
  "tourOperator",
  "clubChain",
  "club",
  "event",
  "other",
] as const;
export type PlatformType = (typeof PlatformTypeValues)[number];

/**
 * Raw date-format preference stored by the backend (`dd/mm/yyyy` order tokens),
 * as edited on the Create/Update Platform pages. Distinct from the display-only
 * {@link DateFormat} abstraction (`"european" | "american" | "iso"`) in
 * `common.types`, which drives how dates are *rendered* — this is the persisted
 * preference value the REST platform API accepts.
 */
export const DateFormatPreferenceValues = ["dd/mm/yyyy", "mm/dd/yyyy", "yyyy/mm/dd"] as const;
export type DateFormatPreference = (typeof DateFormatPreferenceValues)[number];

/**
 * Raw time-format preference stored by the backend (`12H` / `24H`), as edited on
 * the Create/Update Platform pages. Distinct from the display-only
 * {@link TimeFormat} abstraction (`"h12" | "h24"`) in `common.types`.
 */
export const TimeFormatPreferenceValues = ["12H", "24H"] as const;
export type TimeFormatPreference = (typeof TimeFormatPreferenceValues)[number];

/**
 * Geographic scope of a platform, as edited on the Create/Update Platform pages.
 * Mirrors the REST `GeographicScope` enum (`local` | `national` | `international`).
 * Kept as a domain const-array so the form-value types don't depend on the
 * generated REST enum.
 */
export const GeographicScopeValues = ["local", "national", "international"] as const;
export type GeographicScope = (typeof GeographicScopeValues)[number];

/**
 * Legal-entity branch for a platform's legal details. Mirrors the REST
 * `LegalEntityType` enum; drives which legal* fields are populated / sent.
 */
export const LegalEntityTypeValues = ["individual", "company"] as const;
export type LegalEntityType = (typeof LegalEntityTypeValues)[number];

/**
 * Where a platform's Terms & Conditions / Privacy Policy / Cookie Policy text
 * comes from. Mirrors the REST `PlatformLegalDocumentSource` enum: `default`
 * serves the standard Tournated document (no body stored on the platform),
 * `custom` serves a platform-supplied body that replaces it.
 */
export const PlatformLegalDocumentSourceValues = ["default", "custom"] as const;
export type PlatformLegalDocumentSource = (typeof PlatformLegalDocumentSourceValues)[number];

/**
 * How a platform resolves its Sporfie live-streaming key. Mirrors the REST
 * `SporfieMode` enum (inherit the shared key, use a custom key, or disable).
 */
export const SporfieModeValues = ["use_platform_key", "custom_key", "disabled"] as const;
export type SporfieMode = (typeof SporfieModeValues)[number];

/**
 * One rating system's flat toggle set (WTN / DUPR / WPR). Mirrors the REST
 * `LeagueRatingSystem` / `LeagueRatingSystemInput` shape (reused by platforms).
 */
export type PlatformRatingSystem = {
  enabled: boolean;
  requireAccount: boolean;
  displayRating: boolean;
  mandatory: boolean;
};

/** The three rating systems a platform can integrate. */
export type PlatformRatingSystems = {
  wtn: PlatformRatingSystem;
  dupr: PlatformRatingSystem;
  wpr: PlatformRatingSystem;
};

export type PlatformReference = {
  id: PlatformId;
  title: string;
  logo: string | null;
  /**
   * Whether the platform has at least one ACTIVE outgoing platform connection
   * (it shares another platform's data). Only populated by the dashboard
   * platform search/refs endpoints — absent/`null` elsewhere, meaning
   * "unknown". Drives the "Include connected platforms" filter checkbox
   * visibility (`IncludeConnectedPlatformsField`), which falls back to visible
   * when the signal is unknown.
   */
  hasConnections?: boolean | null;
  /**
   * Absolute URL of the platform's own public client site (`null` = none
   * configured). Only populated by the dashboard platform refs endpoint —
   * absent/`null` elsewhere, meaning "unknown". The public-view link machinery
   * (`usePublicViewTarget`) resolves it lazily through that endpoint; don't
   * read it off scope-pair / entity refs.
   */
  link?: string | null;
};

/**
 * Row shape for the dashboard `PlatformsPage` table (REST
 * `GET /api/dashboard/v1/platforms`). Holds identity, the owning organization,
 * sports, the status toggle, and the computed activity / revenue metrics shown
 * as columns. `revenue` and `generatedPlatformFees` are expressed in `currency`
 * (the request's `targetCurrency`, default EUR).
 */
export type PlatformListItem = {
  id: PlatformId;
  title: string;
  abbreviation: string | null;
  logo: string | null;
  status: PlatformStatus;
  owner: OrganizationReference | null;
  dataOwner: OrganizationReference | null;
  sports: SportRef[];
  /**
   * Number of athletes on the platform. **`null` when the caller has no
   * `USER:READ` on this platform** (DEV-4512) — same per-row suppression rule
   * as the money fields. Render a neutral placeholder for `null`; never coerce
   * it to `0`, which would read as a real figure.
   */
  usersCount: number | null;
  leaguesCount: number;
  tournamentsCount: number;
  issuedLicensesCount: number;
  issuedCertificatesCount: number;
  issuedApplicationFormsCount: number;
  /** `null` under the same `USER:READ` suppression rule as `usersCount`. */
  entriesCount: number | null;
  transactionsCount: number;
  /**
   * Total revenue attributed to the platform. **`null` when the caller has no
   * `PLATFORM_FINANCE:READ` on this platform** — the backend nulls the money
   * fields per row rather than dropping the row (DEV-4502). Render a neutral
   * placeholder for `null`; never coerce it to `0`, which would read as a real
   * figure.
   */
  revenue: number | null;
  /** Platform fees generated. `null` under the same suppression rule as `revenue`. */
  generatedPlatformFees: number | null;
  /**
   * Currency of `revenue` / `generatedPlatformFees` (request `targetCurrency`).
   * `null` when the money fields are suppressed (DEV-4512) — never fall back to
   * a default currency for a row whose figures the caller may not read.
   */
  currency: CurrencyCode | null;
  createdAt: Date;
  updatedAt: Date;
};

export type BasePlatform = {
  id: PlatformId;
  title: string;
  platformType: string | null;
  status: string | null;
  countries: CountryCode[];
  languages: Language[];
  timeZone: string;
  dateFormat: DateFormat | null;
  timeFormat: TimeFormat;
  darkEmblem: string | null;
  lightEmblem: string | null;
  currencies: CurrencyCode[];
  platformLink: string | null;
  lightLogo: string | null;
  darkLogo: string | null;
  favicon: string | null;
  brandColor: string | null;
};

export type PlatformWithOwner = {
  owner: Organization & OrganizationWithManagers;
};

export type PlatformWithSports = {
  sports: Sport[];
};

export type PlatformWithDocuments = {
  documents: Document[];
  documentFolders: DocumentFolder[];
};

/**
 * A platform's EFFECTIVE legal identity, as resolved by the backend — the same
 * field set the platform stores itself, plus where it was read from.
 * `source: "entity"` means the platform inherits it from its data-owner entity.
 */
export type PlatformResolvedLegalDetails = {
  source: "platform" | "entity";
  legalEntityType: LegalEntityType | null;
  legalName: string | null;
  legalSurname: string | null;
  legalEmail: string | null;
  legalPhone: string | null;
  personalIdentificationNumber: string | null;
  legalCompanyName: string | null;
  registrationNumber: string | null;
  vatNumber: string | null;
  legalCountry: CountryCode | null;
  legalAddress: string | null;
  signerName: string | null;
  signerSurname: string | null;
  signerRole: string | null;
  signerEmail: string | null;
};

/**
 * Domain shape for the REST platform detail (`GET /api/dashboard/v1/platforms/:id`).
 * Carries every field the Create/Update Platform wizard tabs seed from, grouped by
 * wizard section (owner, general, regional, branding, legal, fees, modules /
 * integrations, domain), plus the documents section and terminology config.
 *
 * `terminology` is `null` when the platform has no stored config; the UI falls back to
 * `DEFAULT_TERMINOLOGY_SETTINGS` at render time.
 */
export type PlatformDetail = {
  id: PlatformId;
  terminology: TerminologySettings | null;

  // Owner section
  /** Display / billing identity federation. `null` on legacy rows with no owner. */
  owner: OrganizationReference | null;
  /** Access-authority federation (drives accessible scope / login membership). `null` when unset. */
  dataOwner: OrganizationReference | null;

  // General section
  title: string;
  type: PlatformType | null;
  abbreviation: string | null;
  description: string | null;
  /** Numeric sport ids (NOT `spr_` public ids). */
  sportIds: number[];
  sports: SportRef[];
  geoScope: GeographicScope | null;

  // Regional section
  countries: CountryCode[];
  /** Locale keys (e.g. `en-GB`). */
  languages: Language[];
  /** IANA timezone identifier (falls back to the local zone when unset). */
  timeZone: string;
  currencies: CurrencyCode[];

  // Branding section — each asset carries its preview URL and the saved storage file id.
  lightLogoUrl: string | null;
  lightLogoFileId: string | null;
  darkLogoUrl: string | null;
  darkLogoFileId: string | null;
  lightEmblemUrl: string | null;
  lightEmblemFileId: string | null;
  darkEmblemUrl: string | null;
  darkEmblemFileId: string | null;
  faviconUrl: string | null;
  faviconFileId: string | null;
  primaryColor: string | null;

  // Legal section
  legalEntityType: LegalEntityType | null;
  legalName: string | null;
  legalSurname: string | null;
  legalEmail: string | null;
  legalPhone: string | null;
  personalIdentificationNumber: string | null;
  legalCompanyName: string | null;
  registrationNumber: string | null;
  vatNumber: string | null;
  legalCountry: CountryCode | null;
  legalAddress: string | null;
  isCurrentUserSigner: boolean;
  signerName: string | null;
  signerSurname: string | null;
  signerRole: string | null;
  signerEmail: string | null;

  // Legal details settings section
  /**
   * The platform inherits its legal identity from its data-owner entity rather
   * than storing one. While this is on, every `legal*` / `signer*` field above
   * is `null` on the wire and the effective values live in
   * {@link PlatformDetail.resolvedLegalDetails}; the backend rejects a write
   * that sends those fields alongside it.
   */
  usesEntityLegalDetails: boolean;
  /**
   * The platform's EFFECTIVE legal identity — its own values, or the entity's
   * when it inherits them. `null` when there is no entity to inherit from, or
   * when the backend withheld the entity's legal record from this user (it is
   * gated on the entity's own read permission, so platform-settings access
   * alone never discloses it).
   */
  resolvedLegalDetails: PlatformResolvedLegalDetails | null;
  termsSource: PlatformLegalDocumentSource;
  /** The platform's own Terms body (rich text). Non-empty only in `custom` mode. */
  termsContent: string;
  privacyPolicySource: PlatformLegalDocumentSource;
  /** The platform's own Privacy Policy body (rich text). Non-empty only in `custom` mode. */
  privacyPolicyContent: string;
  cookiePolicySource: PlatformLegalDocumentSource;
  /** The platform's own Cookie Policy body (rich text). Non-empty only in `custom` mode. */
  cookiePolicyContent: string;

  // Fees section — persisted rows split by service type (the shape the FeesForm seeder consumes).
  fees: { [key in ServiceType]: FeesSegment[] };
  /** Platform-level VAT on the Tournated fee — the last level of the checkout VAT cascade. */
  tournatedFeeVat: TournatedFeeVat;

  // Modules section
  enableToursLeagues: boolean;
  enableRankings: boolean;
  isChatEnabled: boolean;
  onlineBookingCourts: boolean;
  licenseMemberships: boolean;
  liveScoring: boolean;
  liveStreaming: boolean;

  // Extras section — superAdmin-only feature switches. `enableLTS10` /
  // `enableUspaLicenses` each gate a niche tournament-category field across
  // every league on the platform; turning one off clears that field on every
  // category (cascaded server-side). `enableUserCreation` gates the dashboard's
  // create-user affordances instead — attaching existing accounts stays
  // available. Defaults to `true` when the backend omits it.
  // `googleAnalyticsId` is the GA4 measurement id the platform's client site
  // loads next to Tournated's global tag; `null` when none is set.
  enableLTS10: boolean;
  enableUspaLicenses: boolean;
  enableUserCreation: boolean;
  googleAnalyticsId: string | null;

  // Integrations section
  ratingSystems: PlatformRatingSystems | null;
  thirdPartyLicenses: ThirdPartyLicenseReference[];
  sporfieMode: SporfieMode | null;
  /** Custom Sporfie company key (REST `liveStreaming_sporfie_key`). */
  sporfieKey: string | null;

  // Domain section
  alreadyDomain: boolean;
  domain: string | null;
  subDomain: string | null;
  platformLink: string | null;
  landingLink: string | null;

  // Status
  status: PlatformStatus;
} & PlatformWithDocuments;

export type PlatformWithFees = {
  tournatedLicenseFee: number;
  partnerLicenseFee: number;
  fees: {
    [key in ServiceType]: FeesSegment[] | null;
  };
};

export type Platform = {
  id: PlatformId;
  title: string;
  abbreviation: string | null;
  isMobileAppSupported: boolean;
  platformType: string | null;
  status: string | null;
  countries: CountryCode[];
  landingLink: string | null;
  platformLink: string | null;
  signeeRole: string | null;
  signeeFullName: string | null;
  timeZone: string | null;
  currency: string | null;
  description: string | null;
  alreadyDomain: boolean;
  primaryColor: string | null;
  secondaryColor: string | null;
  uploadFile: string | null;
  yourRequest: string | null;
  darkEmblem: string | null;
  lightEmblem: string | null;
  lightLogo: string | null;
  darkLogo: string | null;
  serviceProvider: string | null;
  liveScoring: boolean;
  liveStreaming: boolean;
  onlineBookingCourts: boolean | null;
  onlinePayments: boolean;
  licenseMemberships: boolean;
  onlinePayment: boolean;
  needIntegration: boolean | null;
  needsLanding: boolean;
  companyEmail: string | null;
  fullAddress: string | null;
  registrationNumber: string | null;
  companyName: string | null;
  domain: string | null;
  hasMobileApp: boolean;
  isEnablePrice: boolean | null;
  smm: boolean | null;
  tournatedEntryFee: number | null;
  partnerEntryFee: number | null;
  tournatedLicenseFee: number | null;
  partnerLicenseFee: number | null;
  graphicsForSmm: boolean | null;
  isEnableMaintenance: boolean | null;
  passcode: string | null;
  maintenanceDescription: string | null;
  maintenanceTitle: string | null;
  subDomain: string | null;
  clubs: BaseClub[];
  owner: Organization | null;
  sports: Sport[];
  disciplines: Discipline[];
  thirdPartyLicenses: ThirdPartyLicense[];
  customFee: {
    id: Id;
    rangeStart: number;
    rangeEnd: number;
    feeType: FeeType;
    serviceType: ServiceType;
    tournatedFee: number | null;
    tournatedMinFee: number | null;
    tournatedMaxFee: number | null;
    tournatedMinPercentageFee: number | null;
    tournatedMaxPercentageFee: number | null;
    partnerFee: number | null;
    partnerMinFee: number | null;
    partnerMaxFee: number | null;
    partnerMinPercentageFee: number | null;
    partnerMaxPercentageFee: number | null;
    currency: CurrencyCode | null;
    isMonetization: boolean | null;
    isManagerPayingServiceFee: boolean | null;
  }[];
  templateId: Id | null;
  platformSetting: PlatformSetting | null;
};

export type PlatformSetting = {
  id: Id;
  timeFormatPreference: TimeFormat | null;
  dateFormatPreference: DateFormat | null;
};

export type PlatformWithThirdPartyLicenses = {
  thirdPartyLicenses: ThirdPartyLicense[];
};

// ---------------------------------------------------------------------------
// Create / Update Platform wizard — per-section form-value contracts (C1).
// These describe the RHF form state each wizard step owns; the create page (C2)
// derives its zod schemas from them and the update page (C3) seeds its tab forms
// from the inbound mappers in `platform.mappers.ts`. Fees reuse the shared
// `FeeTabData` / `SubmittedFeeData` types from `@/components/payment` (see the
// `PlatformFees*Values` aliases in `platform.mappers.ts`) rather than a bespoke type.
// ---------------------------------------------------------------------------

/**
 * Owner section — the platform's owner (display / billing identity) and data
 * owner (access authority) federations. The outbound builders read each ref's
 * public id (`fed_…`).
 */
export type PlatformOwnerFormValues = {
  owner: OrganizationReference | null;
  dataOwner: OrganizationReference | null;
};

/** General section — identity, classification, covered sports. */
export type PlatformGeneralFormValues = {
  title: string;
  type: PlatformType | null;
  abbreviation: string;
  /**
   * Selected sports as full refs (matches the org/league convention). The outbound
   * builders map these down to numeric sport ids (`sports.map((s) => s.id)`).
   */
  sports: SportRef[];
};

/**
 * Regional section — geographic scope, locale-key languages (ordered, index 0 is the
 * primary language), IANA time zone, countries and currencies.
 */
export type PlatformRegionalFormValues = {
  geoScope: GeographicScope | null;
  countries: CountryCode[];
  /** Locale keys (e.g. `en-GB`), not legacy language names. Index 0 is the primary language. */
  languages: Language[];
  /** IANA timezone identifier. */
  timeZone: string;
  currencies: CurrencyCode[];
};

/**
 * Branding section — the five image assets as FileUploadField state objects plus
 * the brand colours. `useCustomColorPalette` gates whether the two hex colours are
 * sent (false → cleared on update / omitted on create).
 */
export type PlatformBrandingFormValues = {
  lightLogo: FileUploadFormFieldValue;
  darkLogo: FileUploadFormFieldValue;
  lightEmblem: FileUploadFormFieldValue;
  darkEmblem: FileUploadFormFieldValue;
  favicon: FileUploadFormFieldValue;
  useCustomColorPalette: boolean;
  /**
   * `ColorPickerFormField` writes a React Aria `Color` object, never a hex
   * string. The outbound colour helpers serialize it with
   * `toColor(value).toString("hex")` (yielding `#RRGGBB`) and only send it when
   * `useCustomColorPalette` is true.
   */
  primaryColor: Color;
};

/**
 * Branding storage file ids the page resolves by awaiting its uploads before
 * calling the outbound builders (`null` = no asset / cleared). The builders never
 * upload — they only place these ids on the DTO.
 */
export type PlatformBrandingFileIds = {
  lightLogoFileId: string | null;
  darkLogoFileId: string | null;
  lightEmblemFileId: string | null;
  darkEmblemFileId: string | null;
  faviconFileId: string | null;
};

/**
 * Legal section — mirrors `OrganizationLegalDetailsFormData`: a `legalEntityType`
 * branch with individual and company sub-groups plus the company signer block. The
 * signer is always the named person, so no "current user signs" toggle is collected.
 */
export type PlatformLegalFormValues = {
  /**
   * Inherit the legal identity from the platform's data-owner entity instead of
   * storing one. While it is on, every field below is read-only and shows the
   * entity's values; the save sends the flag ALONE, and the backend clears the
   * platform's own legal columns. Always `false` in the create wizard, which
   * does not render the switch.
   */
  usesEntityDetails: boolean;
  legalEntityType: LegalEntityType;
  individual: {
    name: string;
    surname: string;
    residence: CountryCode | null;
    personalNumber: string;
    email: string;
    address?: string;
    phone: PhoneFormFieldValue;
  };
  company: {
    name: string;
    registrationNumber: string;
    vatNumber: string;
    registrationCountry: CountryCode | null;
    address: string;
    email?: string;
    signerRole: string;
    signerName: string;
    signerSurname: string;
    signerEmail: string;
  };
};

/**
 * Legal documents section — which Terms & Conditions / Privacy Policy / Cookie
 * Policy this platform's users see, and the custom bodies that replace the
 * Tournated defaults. A body is only sent (and only required) while its source
 * is `custom`.
 */
export type PlatformLegalDocumentsFormValues = {
  termsSource: PlatformLegalDocumentSource;
  termsContent: string;
  privacyPolicySource: PlatformLegalDocumentSource;
  privacyPolicyContent: string;
  cookiePolicySource: PlatformLegalDocumentSource;
  cookiePolicyContent: string;
};

/** Modules & integrations section — the platform module toggles plus rating-system + Sporfie settings. */
/**
 * Extras tab — superAdmin-only feature switches.
 *
 * `enableLTS10` / `enableUspaLicenses` each decide whether a niche
 * tournament-category field exists at all across every league on the platform,
 * so turning one off clears that field on every category (cascaded
 * server-side). `enableUserCreation` decides whether the dashboard may create
 * brand-new user accounts on the platform at all. `googleAnalyticsId` is the
 * GA4 measurement id loaded on the platform's client site (`""` = none).
 */
export type PlatformExtrasFormValues = {
  enableLTS10: boolean;
  enableUspaLicenses: boolean;
  enableUserCreation: boolean;
  googleAnalyticsId: string;
};

export type PlatformModulesIntegrationsFormValues = {
  enableToursLeagues: boolean;
  enableRankings: boolean;
  isChatEnabled: boolean;
  onlineBookingCourts: boolean;
  licenseMemberships: boolean;
  liveScoring: boolean;
  liveStreaming: boolean;
  ratingSystems: PlatformRatingSystems;
  enableThirdPartyLicenses: boolean;
  thirdPartyLicenses: ThirdPartyLicenseReference[];
  sporfieMode: SporfieMode;
  /** Custom Sporfie company key; only sent when `sporfieMode` is `custom_key`. */
  sporfieKey: string;
};

/** Domain section — the sub-domain / app / landing links. */
export type PlatformDomainFormValues = {
  subDomain: string;
  platformLink: string;
  landingLink: string;
};

export const PlatformExportColumnValues = [
  "title",
  "abbreviation",
  "status",
  "owner",
  "sports",
  "usersCount",
  "leaguesCount",
  "tournamentsCount",
  "issuedLicensesCount",
  "issuedCertificatesCount",
  "issuedApplicationFormsCount",
  "entriesCount",
  "transactionsCount",
  "revenue",
  "createdAt",
] as const;

export type PlatformExportColumn = (typeof PlatformExportColumnValues)[number];
